/**
 * Sip Yours Accessibility Patch
 * Adds focus trapping, keyboard navigation, and ARIA fixes
 * on top of the existing minified theme.js
 */
(function () {
  'use strict';

  // Focus trap utility
  function trapFocus(container, focusTarget) {
    if (!container) return;
    var sel = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    var elements = Array.from(container.querySelectorAll(sel)).filter(function (el) {
      return el.offsetParent !== null;
    });

    if (!elements.length) return;

    var first = elements[0];
    var last = elements[elements.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === container) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    removeTrapFocus(container);
    container._a11yTrap = handler;
    document.addEventListener('keydown', handler);

    var target = focusTarget || first || container;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  }

  function removeTrapFocus(container) {
    if (container && container._a11yTrap) {
      document.removeEventListener('keydown', container._a11yTrap);
      delete container._a11yTrap;
    }
  }

  // Watch for modals opening (aria-hidden toggled to false on modal-element)
  function watchModals() {
    var modals = document.querySelectorAll('modal-element');
    modals.forEach(function (modal) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName !== 'aria-hidden') return;
          var hidden = modal.getAttribute('aria-hidden');

          if (hidden === 'false') {
            // Modal opened
            var focusId = modal.getAttribute('data-focus-id');
            var focusTarget = focusId ? document.getElementById(focusId) : null;
            setTimeout(function () {
              trapFocus(modal, focusTarget);

              // Escape key to close (for non-blocking modals only)
              var isBlocking = modal.getAttribute('data-blocking');
              if (!isBlocking) {
                function escHandler(e) {
                  if (e.key === 'Escape') {
                    document.removeEventListener('keydown', escHandler);
                    // Trigger close by dispatching to the modal element
                    modal.dispatchEvent(new CustomEvent('theme:modal:close', { bubbles: true }));
                  }
                }
                modal._a11yEsc = escHandler;
                document.addEventListener('keydown', escHandler);
              }
            }, 50);

          } else {
            // Modal closed
            removeTrapFocus(modal);
            if (modal._a11yEsc) {
              document.removeEventListener('keydown', modal._a11yEsc);
              delete modal._a11yEsc;
            }
            // Restore focus to trigger
            if (modal._a11yTrigger && typeof modal._a11yTrigger.focus === 'function') {
              modal._a11yTrigger.focus();
            }
          }
        });
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
  }

  // Watch for drawers opening (aria-expanded toggled on drawer--side divs)
  function watchDrawers() {
    var panels = document.querySelectorAll('.drawer--side');
    panels.forEach(function (panel) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName !== 'aria-expanded') return;
          var expanded = panel.getAttribute('aria-expanded');

          if (expanded === 'true') {
            var focusId = panel.getAttribute('data-focus-id');
            var focusTarget = focusId ? document.getElementById(focusId) : null;
            setTimeout(function () {
              trapFocus(panel, focusTarget);

              // Escape key to close drawer
              function escHandler(e) {
                if (e.key === 'Escape') {
                  document.removeEventListener('keydown', escHandler);
                  panel.dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles: true }));
                  // Also try direct close
                  var drawerEl = panel.closest('drawer-element');
                  if (drawerEl) drawerEl.dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles: true }));
                }
              }
              panel._a11yEsc = escHandler;
              document.addEventListener('keydown', escHandler);
            }, 50);

          } else if (expanded === 'false') {
            removeTrapFocus(panel);
            if (panel._a11yEsc) {
              document.removeEventListener('keydown', panel._a11yEsc);
              delete panel._a11yEsc;
            }
          }
        });
      });
      observer.observe(panel, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  }

  // XMenu: add Escape key to close open submenus
  function patchXMenu() {
    document.querySelectorAll('x-menu-element').forEach(function (menu) {
      menu.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          // Trigger focusout to collapse menu via existing theme logic
          var activeItem = menu.querySelector('[data-item="nav-text"]:focus, [data-item="nav-text"].is-active');
          menu.dispatchEvent(new FocusEvent('focusout', { bubbles: true, relatedTarget: document.body }));
          // Return focus to the triggering nav item
          if (activeItem) activeItem.focus();
        }
      });
    });
  }

  // Popup: track trigger and ensure close button is reachable
  function patchPopups() {
    document.querySelectorAll('popup-block').forEach(function (popup) {
      var observer = new MutationObserver(function () {
        var hidden = popup.getAttribute('aria-hidden');
        if (hidden === 'false') {
          setTimeout(function () {
            var close = popup.querySelector('[class*="close"], [data-item*="close"], button');
            if (close) close.focus();
          }, 100);
        }
      });
      observer.observe(popup, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
  }

  // Initialize everything when DOM is ready
  function init() {
    watchModals();
    watchDrawers();
    patchXMenu();
    patchPopups();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
